import React, { useCallback, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../../css/common/Carousel.css';

const Carousel = ({ 
  items = [], 
  renderItem, 
  showArrows = true, 
  itemsPerView = 1,
  showDots = true,
  className = ''
}) => {
  const [swiper, setSwiper] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (swiper) swiper.slidePrev();
  }, [swiper]);

  const scrollNext = useCallback(() => {
    if (swiper) swiper.slideNext();
  }, [swiper]);

  const scrollTo = useCallback((index) => {
    if (swiper) swiper.slideTo(index);
  }, [swiper]);

  useEffect(() => {
    if (swiper) {
      swiper.on('slideChange', () => {
        setSelectedIndex(swiper.realIndex);
      });
    }
  }, [swiper]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`carousel-container ${className}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={20}
        slidesPerView={Math.min(itemsPerView, items.length)}
        slidesPerGroup={1}
        loop={items.length > itemsPerView}
        speed={600}
        effect="slide"
        onSwiper={setSwiper}
        className="swiper-carousel"
        allowTouchMove={true}
        grabCursor={true}
        breakpoints={{
          320: {
            slidesPerView: Math.min(itemsPerView, items.length, 1),
            spaceBetween: 15
          },
          768: {
            slidesPerView: Math.min(itemsPerView, items.length, 2),
            spaceBetween: 20
          },
          1024: {
            slidesPerView: Math.min(itemsPerView, items.length, 3),
            spaceBetween: 20
          },
          1200: {
            slidesPerView: Math.min(itemsPerView, items.length, 4),
            spaceBetween: 20
          }
        }}
      >
        {items.map((item, index) => (
          <SwiperSlide key={item.id || index} className="swiper-slide">
            {renderItem(item, index)}
          </SwiperSlide>
        ))}
      </Swiper>
      
      {showArrows && items.length > 1 && (
        <>
          <button
            className="carousel-arrow carousel-arrow--prev"
            onClick={scrollPrev}
            aria-label="Previous slide"
          >
            <FaChevronLeft />
          </button>
          
          <button
            className="carousel-arrow carousel-arrow--next"
            onClick={scrollNext}
            aria-label="Next slide"
          >
            <FaChevronRight />
          </button>
        </>
      )}

      {showDots && items.length > 1 && (
        <div className="carousel-dots">
          {items.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === selectedIndex ? 'carousel-dot--active' : ''}`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
